import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["value", "minValue", "maxValue", "orientation", "size"];
import PropTypes from 'prop-types';
import React, { forwardRef, useContext } from 'react';
import { ORIENTATIONS, PROGRESS_METER_SIZES } from '../../constants';
import CustomPropTypes from '../../util/custom-prop-types';
import { FormFieldContext } from '../FormField/FormField.context';
import { StyledProgressBar } from './ProgressBar.styles';

/**
 * @related ['ProgressStepper']
 * @since 8.4.0
 */
export var ProgressBar = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var value = _ref.value,
    minValue = _ref.minValue,
    maxValue = _ref.maxValue,
    orientation = _ref.orientation,
    size = _ref.size,
    rest = _objectWithoutProperties(_ref, _excluded);
  var context = useContext(FormFieldContext);

  // Used to set indicator bar styles. Also ensures % can't be less than 0
  // nor more than 100 */
  var percentComplete = 0;
  if (value >= maxValue) {
    percentComplete = 100;
  } else if (value > minValue && value < maxValue) {
    percentComplete = (value - minValue) / (maxValue - minValue) * 100;
  }
  return /*#__PURE__*/React.createElement(StyledProgressBar, _extends({
    role: "progressbar",
    value: value,
    minValue: minValue,
    maxValue: maxValue,
    orientation: orientation,
    size: size,
    "aria-describedby": context.descriptionId,
    "aria-labelledby": context.labelId,
    "aria-valuenow": value,
    "aria-valuemin": minValue,
    "aria-valuemax": maxValue,
    percentComplete: percentComplete,
    ref: forwardedRef
  }, rest));
});
ProgressBar.displayName = 'ProgressBar';
ProgressBar.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Optional. The `id` of an element that provides information about the progress bar
   * beyond the `aria-label` or `aria-labelledby` label.
   *
   * When `ProgressBar` is used in a `FormField`, this value will automatically be set
   * to the `description` element's `id`.
   */
  'aria-describedby': PropTypes.string,
  /**
   * An accessible label for those using assistive technologies. Describes
   * the purpose of the progress bar.
   *
   * An accessible label is required and can be provided using either `aria-label` or
   * `aria-labelledby`.
   */
  'aria-label': PropTypes.string,
  /**
   * The `id` of the element that acts as an accessible label for those using
   * assistive technologies. The label should describe the purpose of the progress bar.
   *
   * When `ProgressBar` is used in a `FormField`, this value will automatically be set
   * to the `label` element's `id`.
   *
   * An accessible label is required and can be provided using either `aria-label` or
   * `aria-labelledby`.
   */
  'aria-labelledby': PropTypes.string,
  /**
   * The current progress value. Do not use a percentage. Instead, use a
   * number between the minValue and maxValue.
   */
  'value': PropTypes.number.isRequired,
  /**
   * The value when no progress has been made.
   */
  'minValue': PropTypes.number,
  /**
   * The value when progress has been completed.
   */
  'maxValue': PropTypes.number,
  /**
   * Displays either the horizontal or vertical version. Used by the vertical
   * `ProgressStepper`.
   *
   * Supports inline media query objects.
   *
   * @since 8.57.0
   * @ignore
   */
  'orientation': CustomPropTypes.inlineMediaQuery(PropTypes.oneOf(Object.values(ORIENTATIONS))),
  /**
   * Determines the height of the bar.
   */
  'size': PropTypes.oneOf(Object.values(PROGRESS_METER_SIZES))
} : {};
ProgressBar.defaultProps = {
  minValue: 0,
  maxValue: 100,
  orientation: ORIENTATIONS.horizontal,
  size: PROGRESS_METER_SIZES.sm
};